import { createZoneIn } from "../../crud/api/zone_in.crud";
import { defaultZoneIn } from "./utils";

// Minimal RFC-4180-style CSV parser. Handles quoted fields with embedded
// commas, escaped quotes ("" -> "), and newlines inside quotes — needed
// because affirmation copy will contain commas/apostrophes. Returns an
// array of string-arrays (rows of cells). No external dependency.
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const s = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // Flush trailing field/row.
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

// Parse CSV text into row objects keyed by the (lowercased, trimmed)
// header row. Drops fully-blank rows. Expected columns:
//   copy, picture_1, picture_2 (picture_* hold image *filenames*).
export const parseAffirmationCsv = (text) => {
  const rows = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ""));
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] || "").trim();
    });
    return obj;
  });
};

// FileList/array of File -> { lowercased filename: File }.
export const indexFilesByName = (files) => {
  const map = {};
  Array.from(files || []).forEach((f) => {
    map[f.name.toLowerCase()] = f;
  });
  return map;
};

const truthy = (v) => ["1", "true", "yes", "y"].includes(String(v).toLowerCase());

// Inspect parsed rows against the selected image files WITHOUT uploading
// anything, so the modal can show a preview and catch mistakes up front.
// Returns:
//   { total, ready, problems: [{ row, message }], unusedImages: [name] }
// where `ready` is how many rows would actually be created.
export const validateRows = (rows, files) => {
  const filesByName = indexFilesByName(files);
  const usedNames = new Set();
  const problems = [];
  let ready = 0;

  // Detect case-insensitive filename collisions: indexFilesByName keys by
  // lowercased name, so two files like Photo.JPG / photo.jpg would resolve to
  // whichever was selected last. Surface them so the user can rename/re-pick
  // instead of silently importing the wrong image.
  const seenLower = new Set();
  const duplicateImages = [];
  Array.from(files || []).forEach((f) => {
    const key = f.name.toLowerCase();
    if (seenLower.has(key) && !duplicateImages.includes(f.name)) {
      duplicateImages.push(f.name);
    }
    seenLower.add(key);
  });

  rows.forEach((r, i) => {
    const rowNum = i + 2; // 1-based, accounting for the header row
    const copy = (r.copy || "").trim();
    const pic1Name = (r.picture_1 || "").trim();
    const pic2Name = (r.picture_2 || "").trim();

    if (!copy) {
      problems.push({ row: rowNum, message: "missing copy" });
      return;
    }
    if (!pic1Name) {
      problems.push({ row: rowNum, message: "missing picture_1" });
      return;
    }
    if (!filesByName[pic1Name.toLowerCase()]) {
      problems.push({ row: rowNum, message: `image not found: ${pic1Name}` });
      return;
    }
    if (pic2Name && !filesByName[pic2Name.toLowerCase()]) {
      problems.push({ row: rowNum, message: `image not found: ${pic2Name}` });
      return;
    }
    usedNames.add(pic1Name.toLowerCase());
    if (pic2Name) usedNames.add(pic2Name.toLowerCase());
    ready += 1;
  });

  const unusedImages = Array.from(files || [])
    .map((f) => f.name)
    .filter((name) => !usedNames.has(name.toLowerCase()));

  return { total: rows.length, ready, problems, unusedImages, duplicateImages };
};

// Create each affirmation by reusing the existing single-create endpoint
// (multipart → backend uploads the images to S3). Sequential so we can
// report per-row progress and isolate per-row failures. Pass `shouldCancel`
// (a () => boolean) to stop early between rows. Returns
// { created, errors: [{ row, message }], cancelled }.
export const runBulkImport = async ({ rows, files, onProgress, shouldCancel }) => {
  const filesByName = indexFilesByName(files);
  let created = 0;
  const errors = [];
  let cancelled = false;

  for (let i = 0; i < rows.length; i++) {
    if (shouldCancel && shouldCancel()) {
      cancelled = true;
      break;
    }
    const r = rows[i];
    const rowNum = i + 2; // 1-based, accounting for the header row

    const copy = (r.copy || "").trim();
    const pic1Name = (r.picture_1 || "").trim();
    const pic2Name = (r.picture_2 || "").trim();

    if (!copy) {
      errors.push({ row: rowNum, message: "missing copy" });
      onProgress && onProgress(i + 1, rows.length);
      continue;
    }
    const pic1 = pic1Name ? filesByName[pic1Name.toLowerCase()] : null;
    if (!pic1) {
      errors.push({ row: rowNum, message: `image not found: ${pic1Name || "(blank picture_1)"}` });
      onProgress && onProgress(i + 1, rows.length);
      continue;
    }
    const pic2 = pic2Name ? filesByName[pic2Name.toLowerCase()] : null;
    if (pic2Name && !pic2) {
      errors.push({ row: rowNum, message: `image not found: ${pic2Name}` });
      onProgress && onProgress(i + 1, rows.length);
      continue;
    }

    const data = { ...defaultZoneIn, copy, picture_1: pic1 };
    if (pic2) data.picture_2 = pic2;
    if (r.is_pro_athlete) data.is_pro_athlete = truthy(r.is_pro_athlete);

    try {
      await createZoneIn(data);
      created += 1;
    } catch (e) {
      const msg = Array.isArray(e?.errors) ? e.errors.join(", ") : e?.message || "create failed";
      errors.push({ row: rowNum, message: msg });
    }
    onProgress && onProgress(i + 1, rows.length);
  }

  return { created, errors, cancelled };
};
